import React, { useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  BookOpen,
  CalendarDays,
  Edit3,
  FileText,
  LogIn,
  LogOut,
  Plus,
  Save,
  Search,
  Trash2,
  UserRound
} from "lucide-react";
import "./styles.css";

type NoteType = "three_question_protocol" | "blank_recall";

type User = {
  id: number;
  email: string;
  name: string | null;
  picture: string | null;
};

type Note = {
  id: number;
  noteType: NoteType;
  videoTitle: string;
  videoUrl: string;
  inputDate: string;
  answerOne: string;
  answerTwo: string;
  answerThree: string;
  createdAt: string;
  updatedAt: string;
};

type Draft = {
  id?: number;
  noteType: NoteType;
  videoTitle: string;
  videoUrl: string;
  inputDate: string;
  answerOne: string;
  answerTwo: string;
  answerThree: string;
};

const noteLabels: Record<NoteType, { title: string; short: string; prompts: [string, string, string] }> = {
  three_question_protocol: {
    title: "3問プロトコル",
    short: "3問",
    prompts: ["主張は何か？", "根拠は何か？", "どこで使えるか？"]
  },
  blank_recall: {
    title: "視聴後3分の白紙想起",
    short: "白紙想起",
    prompts: ["明日使えること", "それが効く理由", "使う場面"]
  }
};

const emptyDraft = (noteType: NoteType): Draft => ({
  noteType,
  videoTitle: "",
  videoUrl: "",
  inputDate: new Date().toISOString().slice(0, 10),
  answerOne: "",
  answerTwo: "",
  answerThree: ""
});

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...init?.headers
    }
  });

  const contentType = res.headers.get("Content-Type") ?? "";
  if (!contentType.includes("application/json")) {
    throw new Error("ローカルでログイン/APIを確認する場合は npm run dev:pages で起動してください");
  }

  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(data.error ?? "通信に失敗しました");
  }

  return (await res.json()) as T;
}

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewNotice, setPreviewNotice] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<"all" | NoteType>("all");

  useEffect(() => {
    const authError = new URLSearchParams(window.location.search).get("authError");
    if (authError) {
      const detail = new URLSearchParams(window.location.search).get("detail");
      setError(authErrorMessage(authError, detail));
      window.history.replaceState({}, "", window.location.pathname);
    }
    void loadSession(Boolean(authError));
  }, []);

  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch(() => undefined);
    }
  }, []);

  const filteredNotes = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return notes.filter((note) => {
      const matchesType = typeFilter === "all" || note.noteType === typeFilter;
      const haystack = [note.videoTitle, note.videoUrl, note.answerOne, note.answerTwo, note.answerThree].join(" ").toLowerCase();
      return matchesType && (!needle || haystack.includes(needle));
    });
  }, [notes, query, typeFilter]);

  async function loadSession(preserveError = false) {
    setLoading(true);
    if (!preserveError) {
      setError(null);
    }
    setPreviewNotice(null);
    try {
      const data = await api<{ user: User | null }>("/api/auth/me");
      setUser(data.user);
      if (data.user) {
        setError(null);
        setPreviewNotice(null);
        const list = await api<{ notes: Note[] }>("/api/notes");
        setNotes(list.notes);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "読み込みに失敗しました";
      if (message.includes("npm run dev:pages")) {
        setPreviewNotice(message);
        setUser(null);
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  }

  async function loadNotes() {
    const list = await api<{ notes: Note[] }>("/api/notes");
    setNotes(list.notes);
  }

  async function saveDraft(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!draft) return;
    setSaving(true);
    setError(null);
    try {
      const method = draft.id ? "PUT" : "POST";
      const path = draft.id ? `/api/notes/${draft.id}` : "/api/notes";
      await api<{ note: Note }>(path, {
        method,
        body: JSON.stringify(draft)
      });
      await loadNotes();
      setDraft(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "保存に失敗しました");
    } finally {
      setSaving(false);
    }
  }

  async function deleteNote(note: Note) {
    const ok = window.confirm(`「${note.videoTitle || noteLabels[note.noteType].title}」を削除しますか？`);
    if (!ok) return;
    setError(null);
    try {
      await api<{ ok: true }>(`/api/notes/${note.id}`, { method: "DELETE" });
      await loadNotes();
      if (draft?.id === note.id) setDraft(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "削除に失敗しました");
    }
  }

  function editNote(note: Note) {
    setDraft({
      id: note.id,
      noteType: note.noteType,
      videoTitle: note.videoTitle,
      videoUrl: note.videoUrl,
      inputDate: note.inputDate,
      answerOne: note.answerOne,
      answerTwo: note.answerTwo,
      answerThree: note.answerThree
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  if (loading) {
    return (
      <main className="min-h-screen bg-paper text-ink">
        <div className="mx-auto flex min-h-screen max-w-5xl items-center justify-center px-5">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-line border-t-gold" />
        </div>
      </main>
    );
  }

  if (!user) {
    return <LoginScreen error={error} previewNotice={previewNotice} />;
  }

  return (
    <main className="min-h-screen bg-paper text-ink">
      <header className="border-b border-line bg-ink text-paper">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-paper text-ink">
              <BookOpen size={24} />
            </div>
            <div className="min-w-0">
              <h1 className="truncate text-xl font-semibold tracking-normal">D-Lab Note</h1>
              <p className="truncate text-sm text-paper/70">学んだことを実践へつなげる</p>
            </div>
          </div>
          <a
            href="/api/auth/logout"
            className="inline-flex h-10 shrink-0 items-center gap-2 rounded-md border border-paper/20 px-3 text-sm font-medium hover:bg-paper/10"
          >
            <LogOut size={18} />
            <span className="hidden sm:inline">ログアウト</span>
          </a>
        </div>
      </header>

      <div className="mx-auto grid max-w-6xl gap-6 px-4 py-5 sm:px-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <section className="space-y-5">
          <UserBar user={user} />
          {error ? <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div> : null}
          <ActionPanel onNew={setDraft} />
          {draft ? (
            <NoteForm
              draft={draft}
              onChange={setDraft}
              onCancel={() => setDraft(null)}
              onSubmit={saveDraft}
              saving={saving}
            />
          ) : null}
          <NotesList
            notes={filteredNotes}
            query={query}
            typeFilter={typeFilter}
            onQuery={setQuery}
            onTypeFilter={setTypeFilter}
            onEdit={editNote}
            onDelete={deleteNote}
          />
        </section>

        <aside className="space-y-4 lg:sticky lg:top-5 lg:self-start">
          <GuideBlock type="three_question_protocol" />
          <GuideBlock type="blank_recall" />
        </aside>
      </div>
    </main>
  );
}

function authErrorMessage(code: string, detail: string | null) {
  const messages: Record<string, string> = {
    oauth_config: "Google OAuth設定が未完了です。.dev.vars に GOOGLE_CLIENT_ID と GOOGLE_CLIENT_SECRET を設定してください。",
    oauth_validation: "Googleログインを最初からやり直してください。認証用の一時情報が確認できませんでした。",
    google_token: `Google認証に失敗しました。Googleの返答: ${detail ?? "token_exchange_failed"}。クライアントシークレットと保存状態を確認してください。`,
    google_profile: "Googleプロフィールを取得できませんでした。",
    google_email: "Googleアカウントのメールアドレスを取得できませんでした。"
  };
  return messages[code] ?? "Googleログインに失敗しました。";
}

function LoginScreen({ error, previewNotice }: { error: string | null; previewNotice: string | null }) {
  return (
    <main className="min-h-screen bg-paper text-ink">
      <div className="mx-auto flex min-h-screen max-w-4xl flex-col justify-center px-5 py-10">
        <section className="grid gap-8 md:grid-cols-[1fr_0.8fr] md:items-center">
          <div>
            <div className="mb-6 inline-flex items-center gap-3 rounded-full border border-line bg-white/60 px-4 py-2 text-sm text-moss">
              <BookOpen size={18} />
              D-Lab Note
            </div>
            <h1 className="text-4xl font-semibold leading-tight tracking-normal sm:text-5xl">
              学びを、明日の行動に残す。
            </h1>
            <p className="mt-5 max-w-xl text-base leading-8 text-ink/70">
              3問プロトコルと白紙想起を、動画タイトル・日付と一緒に保存する個人用ノートです。
            </p>
            {error ? <p className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p> : null}
            {previewNotice ? (
              <p className="mt-4 rounded-md border border-line bg-white/70 px-4 py-3 text-sm leading-6 text-ink/70">
                見た目確認モードで表示中です。ログインと保存まで確認するときは <code>npm run dev:pages</code> で起動してください。
              </p>
            ) : null}
            <a
              href="/api/auth/login"
              className="mt-7 inline-flex h-12 items-center gap-3 rounded-md bg-ink px-5 font-semibold text-paper shadow-soft hover:bg-ink/90"
            >
              <LogIn size={20} />
              Googleでログイン
            </a>
          </div>
          <div className="rounded-lg border border-line bg-white/70 p-5 shadow-soft">
            <div className="rounded-md border border-line bg-paper p-5">
              <p className="text-sm font-semibold text-gold">WORK 01</p>
              <h2 className="mt-2 text-2xl font-semibold">3問プロトコル</h2>
              <div className="mt-4 space-y-3 text-sm text-ink/75">
                <p>主張は何か？</p>
                <p>根拠は何か？</p>
                <p>どこで使えるか？</p>
              </div>
            </div>
            <div className="mt-4 rounded-md border border-line bg-paper p-5">
              <p className="text-sm font-semibold text-gold">WORK 02</p>
              <h2 className="mt-2 text-2xl font-semibold">3分の白紙想起</h2>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}

function UserBar({ user }: { user: User }) {
  return (
    <section className="flex items-center gap-3 rounded-lg border border-line bg-white/80 p-4">
      {user.picture ? (
        <img src={user.picture} alt="" className="h-10 w-10 rounded-full" />
      ) : (
        <div className="grid h-10 w-10 place-items-center rounded-full bg-moss text-paper">
          <UserRound size={20} />
        </div>
      )}
      <div className="min-w-0">
        <p className="truncate font-semibold">{user.name || "ユーザー"}</p>
        <p className="truncate text-sm text-ink/60">{user.email}</p>
      </div>
    </section>
  );
}

function ActionPanel({ onNew }: { onNew: (draft: Draft) => void }) {
  return (
    <section className="grid gap-3 sm:grid-cols-2">
      <button
        type="button"
        onClick={() => onNew(emptyDraft("three_question_protocol"))}
        className="flex min-h-24 items-center justify-between rounded-lg border border-line bg-white p-4 text-left shadow-soft hover:border-gold"
      >
        <span>
          <span className="block text-sm font-semibold text-gold">WORK 01</span>
          <span className="mt-1 block text-xl font-semibold">3問プロトコル</span>
        </span>
        <Plus size={24} />
      </button>
      <button
        type="button"
        onClick={() => onNew(emptyDraft("blank_recall"))}
        className="flex min-h-24 items-center justify-between rounded-lg border border-line bg-white p-4 text-left shadow-soft hover:border-gold"
      >
        <span>
          <span className="block text-sm font-semibold text-gold">WORK 02</span>
          <span className="mt-1 block text-xl font-semibold">白紙想起</span>
        </span>
        <Plus size={24} />
      </button>
    </section>
  );
}

function NoteForm({
  draft,
  onChange,
  onCancel,
  onSubmit,
  saving
}: {
  draft: Draft;
  onChange: (draft: Draft) => void;
  onCancel: () => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  saving: boolean;
}) {
  const meta = noteLabels[draft.noteType];
  const setField = (field: keyof Draft, value: string) => onChange({ ...draft, [field]: value });

  return (
    <form onSubmit={onSubmit} className="rounded-lg border border-line bg-white p-4 shadow-soft sm:p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-gold">{draft.id ? "編集" : "新規作成"}</p>
          <h2 className="mt-1 text-2xl font-semibold">{meta.title}</h2>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={onCancel} className="h-10 rounded-md border border-line px-4 text-sm font-semibold hover:bg-paper">
            閉じる
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex h-10 items-center gap-2 rounded-md bg-ink px-4 text-sm font-semibold text-paper hover:bg-ink/90 disabled:opacity-50"
          >
            <Save size={17} />
            {saving ? "保存中" : "保存"}
          </button>
        </div>
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-[1fr_180px]">
        <div className="space-y-4">
          <label className="block">
            <span className="text-sm font-semibold">動画タイトル</span>
            <input
              value={draft.videoTitle}
              onChange={(event) => setField("videoTitle", event.target.value)}
              required
              className="mt-2 h-12 w-full rounded-md border border-line bg-paper px-3 outline-none focus:border-gold"
              placeholder="例：集中力を高める方法"
            />
          </label>
          <label className="block">
            <span className="text-sm font-semibold">動画リンク</span>
            <input
              type="text"
              inputMode="url"
              value={draft.videoUrl}
              onChange={(event) => setField("videoUrl", event.target.value)}
              className="mt-2 h-12 w-full rounded-md border border-line bg-paper px-3 outline-none focus:border-gold"
              placeholder="https://example.com/video"
            />
          </label>
        </div>
        <label className="block">
          <span className="text-sm font-semibold">入力日付</span>
          <input
            type="date"
            value={draft.inputDate}
            onChange={(event) => setField("inputDate", event.target.value)}
            required
            className="mt-2 h-12 w-full rounded-md border border-line bg-paper px-3 outline-none focus:border-gold"
          />
        </label>
      </div>

      <div className="mt-5 space-y-4">
        <Textarea label={meta.prompts[0]} value={draft.answerOne} onChange={(value) => setField("answerOne", value)} />
        <Textarea label={meta.prompts[1]} value={draft.answerTwo} onChange={(value) => setField("answerTwo", value)} />
        <Textarea label={meta.prompts[2]} value={draft.answerThree} onChange={(value) => setField("answerThree", value)} />
      </div>
    </form>
  );
}

function Textarea({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold">{label}</span>
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        rows={4}
        className="mt-2 w-full resize-y rounded-md border border-line bg-paper px-3 py-3 leading-7 outline-none focus:border-gold"
      />
    </label>
  );
}

function NotesList({
  notes,
  query,
  typeFilter,
  onQuery,
  onTypeFilter,
  onEdit,
  onDelete
}: {
  notes: Note[];
  query: string;
  typeFilter: "all" | NoteType;
  onQuery: (value: string) => void;
  onTypeFilter: (value: "all" | NoteType) => void;
  onEdit: (note: Note) => void;
  onDelete: (note: Note) => void;
}) {
  return (
    <section>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-2xl font-semibold">ノート一覧</h2>
        <div className="flex flex-col gap-2 sm:flex-row">
          <label className="relative block">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/45" size={18} />
            <input
              value={query}
              onChange={(event) => onQuery(event.target.value)}
              placeholder="検索"
              className="h-10 w-full rounded-md border border-line bg-white pl-10 pr-3 outline-none focus:border-gold sm:w-56"
            />
          </label>
          <select
            value={typeFilter}
            onChange={(event) => onTypeFilter(event.target.value as "all" | NoteType)}
            className="h-10 rounded-md border border-line bg-white px-3 outline-none focus:border-gold"
          >
            <option value="all">すべて</option>
            <option value="three_question_protocol">3問プロトコル</option>
            <option value="blank_recall">白紙想起</option>
          </select>
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {notes.length === 0 ? (
          <div className="rounded-lg border border-dashed border-line bg-white/70 p-8 text-center text-ink/60">
            まだノートがありません。
          </div>
        ) : (
          notes.map((note) => (
            <article key={note.id} className="rounded-lg border border-line bg-white p-4 shadow-soft">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 text-sm text-ink/60">
                    <span className="rounded-full bg-paper px-3 py-1 font-semibold text-gold">{noteLabels[note.noteType].short}</span>
                    <span className="inline-flex items-center gap-1">
                      <CalendarDays size={15} />
                      {note.inputDate}
                    </span>
                  </div>
                  <h3 className="mt-3 break-words text-xl font-semibold">{note.videoTitle}</h3>
                  {note.videoUrl ? (
                    <a
                      href={toExternalUrl(note.videoUrl)}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 block break-words text-sm font-semibold text-moss underline decoration-moss/30 underline-offset-4 hover:text-ink"
                    >
                      {note.videoUrl}
                    </a>
                  ) : null}
                  <p className="mt-2 line-clamp-2 whitespace-pre-wrap text-sm leading-6 text-ink/70">{note.answerOne}</p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <button
                    type="button"
                    onClick={() => onEdit(note)}
                    className="inline-flex h-10 items-center gap-2 rounded-md border border-line px-3 text-sm font-semibold hover:bg-paper"
                  >
                    <Edit3 size={16} />
                    編集
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(note)}
                    className="grid h-10 w-10 place-items-center rounded-md border border-line text-red-700 hover:bg-red-50"
                    aria-label="削除"
                    title="削除"
                  >
                    <Trash2 size={17} />
                  </button>
                </div>
              </div>
            </article>
          ))
        )}
      </div>
    </section>
  );
}

function toExternalUrl(value: string) {
  return /^[a-z][a-z\d+\-.]*:/i.test(value) ? value : `https://${value}`;
}

function GuideBlock({ type }: { type: NoteType }) {
  const meta = noteLabels[type];
  return (
    <section className="rounded-lg border border-line bg-white/75 p-4">
      <div className="flex items-center gap-2 text-gold">
        <FileText size={18} />
        <p className="text-sm font-semibold">{meta.title}</p>
      </div>
      <ol className="mt-3 space-y-3 text-sm leading-6 text-ink/70">
        {meta.prompts.map((prompt, index) => (
          <li key={prompt}>
            <span className="font-semibold text-ink">STEP {index + 1}</span> {prompt}
          </li>
        ))}
      </ol>
    </section>
  );
}

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
